import {AuiIf, ThreadPrimitive, useAui} from '@assistant-ui/react';
import {
  Banner,
  Button,
  CalloutIcon,
  DotProgress,
  Icon,
  Link,
} from '@wandb/components';
import React, {type FC, useCallback, useEffect, useRef, useState} from 'react';

import {useActiveThreadLoadState} from '../../agentChat/store/context';
import {useDismissibleNotice} from '../../agentChat/useDismissibleNotice';
import {ErrorBoundary} from '../ErrorBoundary';
import {WBAgentComposer} from './WBAgentComposer';
import {AssistantMessage} from './WBAgentMessageAssistant';
import {UserMessage} from './WBAgentMessageUser';
import {SuggestionItem} from './WBAgentSuggestionItem';

const messageComponents = {UserMessage, AssistantMessage};

const WBAGENT_DEMO_VIDEO_URL = 'https://www.youtube.com/watch?v=F3xwB3GQKkk';

export const WBAgentThread = () => {
  const {
    activeThreadId,
    isLoading,
    error: loadError,
  } = useActiveThreadLoadState();

  const aui = useAui();

  const {dismissed: specialWelcomeDismissed, dismiss: dismissSpecialWelcome} =
    useDismissibleNotice('wbagent-special-welcome');
  const [isFirstVisit] = useState(() => !specialWelcomeDismissed);
  useEffect(() => {
    if (isFirstVisit) {
      dismissSpecialWelcome();
    }
  }, [isFirstVisit, dismissSpecialWelcome]);

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <DotProgress aria-label="Loading thread" />
      </div>
    );
  }

  if (loadError && activeThreadId) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-12 px-16">
        <CalloutIcon icon="warning" color="red" size="md" />
        <span className="font-semibold text-base">
          Failed to load conversation
        </span>
        <Button
          variant="secondary"
          startIcon="reload-refresh"
          onPress={() => aui.threads().switchToThread(activeThreadId)}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <ThreadPrimitive.Root
      className="flex flex-1 min-h-0 flex-col"
      style={{
        ['--thread-max-width' as string]: '44rem',
      }}>
      <ThreadPrimitive.ViewportProvider>
        <ThreadPrimitive.Viewport
          turnAnchor="top"
          autoScroll
          className="flex flex-1 flex-col overflow-y-auto scroll-smooth overscroll-contain pl-24 pr-12 pt-12 [scrollbar-gutter:stable] [scrollbar-width:thin] [scrollbar-color:#0d0f1252_transparent]">
          <MessageRecoveryBoundary>
            <AuiIf condition={s => s.thread.isEmpty}>
              <div className="flex flex-1 flex-col items-center justify-center gap-8">
                <div className="flex items-center gap-8 mt-24 mb-24">
                  <Icon
                    aria-label="workspace-agent-logo"
                    icon="lightboard-solid"
                    color="teal"
                  />
                  <span className="text-[20px] font-semibold">
                    {isFirstVisit
                      ? "Hi, I'm W&B Agent"
                      : 'How can I help you today?'}
                  </span>
                </div>
                {isFirstVisit && (
                  <span className="max-w-lg text-pretty text-center text-moon-650">
                    I can summarize projects, recommend experiments, spot
                    patterns, and build reports for you.
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-8">
                <ThreadPrimitive.Suggestions
                  components={{Suggestion: SuggestionItem}}
                />
              </div>
              <Banner variant="info" isDismissible={false} mt={5}>
                <div className="flex w-full items-center justify-between gap-8">
                  <span className="text-[15px]">New to W&B Agent?</span>
                  <Link
                    href={WBAGENT_DEMO_VIDEO_URL}
                    target="_blank"
                    rel="noopener noreferrer">
                    <span className="night-aware text-[15px] font-semibold text-blue-650">
                      Watch a 4-min demo
                    </span>
                    <Icon icon="forward-next" size="sm" color="blue" />
                  </Link>
                </div>
              </Banner>
            </AuiIf>
            <div className="flex flex-col shrink-0">
              <ThreadPrimitive.Messages components={messageComponents} />
            </div>
          </MessageRecoveryBoundary>
        </ThreadPrimitive.Viewport>
        <div className="px-24 py-16">
          <WBAgentComposer />
        </div>
      </ThreadPrimitive.ViewportProvider>
    </ThreadPrimitive.Root>
  );
};

/**
 * Auto-recovers from AUI tearing crashes during thread switches [1] by
 * remounting children with fresh subscriptions. Allows one recovery per thread
 * switch; a second error re-throws from `renderError` (error boundaries cannot
 * catch their own render errors) so it bubbles to `AgentChatProvider`.
 *
 * [1] https://github.com/assistant-ui/assistant-ui/issues/3652
 */
const MessageRecoveryBoundary: FC<{children: React.ReactNode}> = ({
  children,
}) => {
  const {activeThreadId} = useActiveThreadLoadState();
  const [recoveryKey, setRecoveryKey] = useState(0);
  const recovered = useRef(false);

  // Reset on thread switch — uses the store value (tick 1), which updates
  // before AUI processes the committed switch (tick 2).
  const prevThreadId = useRef(activeThreadId);
  if (prevThreadId.current !== activeThreadId) {
    prevThreadId.current = activeThreadId;
    recovered.current = false;
  }

  const handleError = useCallback(() => {
    if (recovered.current) {
      return;
    }

    recovered.current = true;

    // Wait one frame so AUI's microtask scheduler finishes flushing before
    // remounting children with new subscriptions.
    requestAnimationFrame(() => setRecoveryKey(k => k + 1));
  }, []);

  const renderError = useCallback((error: Error) => {
    if (recovered.current) {
      throw error; // Propagate a second error up to the next boundary.
    }

    return <></>;
  }, []);

  return (
    <ErrorBoundary
      key={recoveryKey}
      silent
      onError={handleError}
      renderError={renderError}>
      {children}
    </ErrorBoundary>
  );
};
